import React, {useLayoutEffect, useReducer} from 'react';
import * as PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import {
    Checkbox,
    colors,
    Paper,
    Table,
    TableBody,
    TableCell,
    TablePagination,
    TableRow,
    Tooltip
} from '@material-ui/core';
import moment from "moment";
import EnhancedTableToolbar from "./EnhancedTableToolbar";
import EnhancedTableHead from "./EnhancedTableHead";
import * as classnames from "classnames";
import {WarningTwoTone} from "@material-ui/icons";
import reducer from '../../utils/defaultReducer';

const styles = theme => ({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flex: 1,
        flexDirection: 'column'
    },
    table: {
        minWidth: 1020,
    },
    tbody: {
        overflowY: 'auto',
    },
    tableWrapper: {
        overflowX: 'auto',
        flex: 1,
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 266px)',
    },
    row: {
        '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)'
        }
    },
    rowError: {
        '&.MuiTableRow-root': {
            // .light (não .main): a linha inteira fica vermelha e o texto
            // padrão da tabela é escuro — .main derruba o contraste pra
            // 4.34:1 (abaixo de AA); .light mantém ~5.64:1, igual ao antigo.
            backgroundColor: theme.palette.error.light,
            '&.MuiTableRow-hover:hover': {
                backgroundColor: theme.palette.error.main
            }
        }
    },
    // Sem laranja na marca: reaproveita o amarelo já usado no gauge de horas
    // (Chart/utils.js) para o nível "alerta", mais brando que "error".
    rowAlert: {
        '&.MuiTableRow-root': {
            backgroundColor: '#ffee58',
            '&.MuiTableRow-hover:hover': {
                backgroundColor: colors.yellow[200]
            }
        }
    },
    tooltipIcon: {
        color: theme.palette.text.secondary,
        margin: 'auto 0',
    },
    progressBar: {
        width: 250,
        padding: '20px 5%',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        }
    },
});

function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}


function getFormatedDate(value, item) {
    if (value.format) {
        let newFormat = moment(value.field ? item[value.field] : item[value.id]);
        return newFormat.isValid() ? newFormat.format(value.format) : '';
    }
    return item[value.id];
};

function Index(props) {


    const initialState = {
        order: props.order,
        orderBy: props.orderBy,
        selected: [],
        page: 0,
        rowsPerPage: 25,
        data: props.data,
    };

    const [state, setState] = useReducer(reducer, initialState, () => initialState);

    useLayoutEffect(() => {
        if (props.data !== state.data) {
            setState({
                data: props.data,
                page: 0,
            });
        }
    }, [props.data])

    const renderCell = props.renderCell ?? ((item, rows, classes) => rows.map((value, i) => {
            return <TableCell align="left" key={i} padding={'none'} onClick={() => props?.onClickRow(item)}>
                {
                    (value.bool && (item[value.id] === 'N' ? <Tooltip
                        title={item.mensagemUltimoApontamento ?? 'não há mensagem'}
                        placement={'top'}
                        className={classes.tooltipIcon}>
                        <WarningTwoTone style={{marginTop: 5}}/>
                    </Tooltip> : <></>))
                    ||
                    getFormatedDate(value, item)
                }
            </TableCell>
        }
    ))

    const handleRequestSort = (event, property) => {
        const orderBy = property;
        let order = 'desc';

        if (state.orderBy === property && state.order === 'desc') {
            order = 'asc';
        }

        setState({
            order,
            orderBy,
        });
    };

    const handleSelectAllClick = (event, idList) => {
        if (event.target.checked) {
            props.setSelected(state.data.map(item => idList(item)));
            return;
        }
        props.setSelected([]);
    };

    const handleClick = (event, id) => {
        const {selected} = props;
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        props.setSelected(newSelected);
    };

    const handleChangePage = (event, page) => {
        setState({page});
    };

    const handleChangeRowsPerPage = event => {
        setState({
            rowsPerPage: event.target.value,
            page: 0,
        });
    };

    function isSelectedRow (id) {
        return props.selected.indexOf(id) !== -1;
    }



    const {selected, classes, rows} = props;
    const {order, orderBy, rowsPerPage, page, data} = state;
    return (
        <Paper className={classes.root}>
            <EnhancedTableToolbar tableTitle={props.tableTitle} numSelected={selected.length}
                                  topActions={props.topActions} selectActions={props.selectActions}/>
            <div className={classes.tableWrapper}>
                <Table className={classes.table} aria-labelledby="tableTitle" size={'small'}>
                    <EnhancedTableHead
                        {...props}
                        numSelected={selected.length}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={(e) => handleSelectAllClick(e, props.idList)}
                        onRequestSort={handleRequestSort}
                        rowCount={data.length}
                        firstColumn={props.firstColumn}
                        rows={props.rows}
                    />
                    <TableBody className={classnames(classes.tbody)}>
                        {stableSort(data, getSorting(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map(item => {
                                const id = props.idList(item);
                                const isSelected = isSelectedRow(id);
                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        tabIndex={-1}
                                        key={id}
                                        selected={isSelected}
                                        className={classnames(item.status === 'error' ? classes.rowError : '', item.status === 'alert' ? classes.rowAlert : '', classes.row)}
                                    >
                                        {
                                            (props.checkbox || props.firstColumn.component) &&
                                            <TableCell
                                                padding="checkbox"
                                                style={{maxWidth: 150, padding: '5px 0'}}
                                            >

                                                <div style={{
                                                    // width:50,
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    alignContent: 'center'
                                                }}>
                                                    {
                                                        props.checkbox &&
                                                        <Checkbox style={{width: 50}} checked={isSelected}
                                                                  onClick={event => handleClick(event, id)}/>
                                                    }
                                                    {
                                                        props.firstColumn.component && props.firstColumn.component(state, props, id, item)
                                                    }
                                                </div>
                                            </TableCell>
                                        }
                                        {
                                            renderCell(item, rows, classes)
                                        }

                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </div>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={'Qtde.'}
                labelDisplayedRows={({from, to, count}) => `${from}-${to} / ${count}`}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}

                backIconButtonProps={{
                    'aria-label': 'Página Anterior',
                }}
                nextIconButtonProps={{
                    'aria-label': 'Próxima Página',
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

Index.propTypes = {
    firstColumn: PropTypes.object,
    idList: PropTypes.func,
    renderCell: PropTypes.func,
    onClickRow: PropTypes.func,
    selectActions: PropTypes.element,
    topActions: PropTypes.any,
    selected: PropTypes.array,
    setSelected: PropTypes.func,
    orderBy: PropTypes.string,
    order: PropTypes.string,
    funcs: PropTypes.object
};


Index.defaultProps = {
    firstColumn: {},
    funcs: {},
    selected: [],
    order: 'asc',
    orderBy: 'data',
    setSelected: () => {
    },
    selectActions: <></>,
    onClickRow: () => {
        console.log('onClickRow')
    },
    idList: (item) => item.id,
};

export default withStyles(styles)(Index);
